import { toast } from 'sonner';

// Achievement milestone definitions
const milestones = {
  content_created_count: {
    1: { title: 'First Content!', message: 'You created your first piece of content!', icon: '✨' },
    5: { title: 'Getting Started!', message: "You've created 5 pieces of content!", icon: '🔥' },
    10: { title: 'Content Creator!', message: '10 pieces of content created!', icon: '🏆' },
    25: { title: 'Content Machine!', message: "25 pieces created - you're on fire!", icon: '🚀' },
    50: { title: 'Content Master!', message: '50 pieces of viral content!', icon: '👑' },
    100: { title: 'Content Legend!', message: '100 pieces! Absolutely legendary!', icon: '⭐' },
  },
  offers_created_count: {
    1: { title: 'First Offer!', message: 'You built your first digital offer!', icon: '📦' },
    3: { title: 'Offer Builder!', message: '3 offers created!', icon: '💼' },
    5: { title: 'Offer Expert!', message: '5 offers - building that product empire!', icon: '🏆' },
  },
  sales_pages_created_count: {
    1: { title: 'First Sales Page!', message: 'Your sales page is ready!', icon: '📄' },
    3: { title: 'Sales Page Pro!', message: '3 sales pages created!', icon: '🏆' },
  },
  email_campaigns_created_count: {
    1: { title: 'First Email Campaign!', message: 'Your email campaign is ready!', icon: '📧' },
    3: { title: 'Email Marketer!', message: '3 campaigns created!', icon: '🏆' },
  },
  daily_login_streak: {
    3: { title: '3-Day Streak!', message: 'You\'re building momentum!', icon: '🔥' },
    7: { title: 'Week Warrior!', message: '7 days in a row - amazing!', icon: '💪' },
    14: { title: 'Two Week Champion!', message: '14 days of consistency!', icon: '🏆' },
    30: { title: 'Month Master!', message: '30 days - unstoppable!', icon: '👑' },
    60: { title: 'Diamond Streak!', message: '60 days of dedication!', icon: '💎' },
    100: { title: 'Legendary Streak!', message: '100 days! You\'re a legend!', icon: '⭐' },
  }
};

// Store checked milestones to avoid duplicate toasts
const checkedMilestones = new Map();

/**
 * Check if user has reached a milestone and show achievement toast
 * @param {string} counterField - The field name (e.g., 'content_created_count')
 * @param {number} newValue - The new value of the counter
 */
export const checkMilestone = (counterField, newValue) => {
  const fieldMilestones = milestones[counterField];
  if (!fieldMilestones) return;

  const milestone = fieldMilestones[newValue];
  if (!milestone) return;

  // Create unique key for this milestone
  const milestoneKey = `${counterField}-${newValue}`;
  
  // Check if we've already shown this milestone
  if (checkedMilestones.has(milestoneKey)) return;
  
  // Mark as shown
  checkedMilestones.set(milestoneKey, true);

  // Show celebration toast
  toast.custom(
    (t) => (
      <div className="bg-gradient-to-r from-purple-600 to-pink-600 rounded-xl p-4 shadow-lg shadow-purple-500/30 max-w-sm">
        <div className="flex items-start space-x-3">
          <span className="text-3xl">{milestone.icon}</span>
          <div>
            <h4 className="text-white font-bold text-lg">{milestone.title}</h4>
            <p className="text-white/80 text-sm">{milestone.message}</p>
          </div>
        </div>
      </div>
    ),
    {
      duration: 5000,
      position: 'top-center',
    }
  );
};

/**
 * Check all user counters for milestones (called on app load/user fetch)
 * @param {object} user - User object with counters
 * @param {object} prevUser - Previous user object to compare
 */
export const checkUserMilestones = (user, prevUser = null) => {
  if (!user) return;

  const countersToCheck = [
    'content_created_count',
    'offers_created_count',
    'sales_pages_created_count',
    'email_campaigns_created_count',
    'daily_login_streak'
  ];

  countersToCheck.forEach(counter => {
    const currentValue = user[counter] || 0;
    const prevValue = prevUser?.[counter] || 0;

    // Only check if value increased
    if (currentValue > prevValue) {
      checkMilestone(counter, currentValue);
    }
  });
};

/**
 * Show a custom achievement toast
 * @param {string} title - Achievement title
 * @param {string} message - Achievement message
 * @param {string} icon - Emoji icon
 */
export const showAchievement = (title, message, icon = '🎉') => {
  toast.custom(
    (t) => (
      <div className="bg-gradient-to-r from-purple-600 to-pink-600 rounded-xl p-4 shadow-lg shadow-purple-500/30 max-w-sm">
        <div className="flex items-start space-x-3">
          <span className="text-3xl">{icon}</span>
          <div>
            <h4 className="text-white font-bold text-lg">{title}</h4>
            <p className="text-white/80 text-sm">{message}</p>
          </div>
        </div>
      </div>
    ),
    {
      duration: 5000,
      position: 'top-center',
    }
  );
};

export default { checkMilestone, checkUserMilestones, showAchievement };
